// Example data for the walkthrough, in the frontend's camelCase shape. The practice source hands
// these objects out as they are; no refactor helper runs on them. Every id starts with `practice-`,
// so the request guard in http.service.js recognises it. Dates are offsets from the run start: a
// negative number is in the past, a positive number is in the future.

import { QUESTION_TYPES } from 'src/services/consts/ticket.const'

// Where each screen of the walkthrough stands. Cards name one of these in their scene.
export const PRACTICE_STAGE = {
  NONE: 'none',
  SELECTED: 'selected',
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
}

// The request the walkthrough creates, submits, and follows.
export const PRACTICE_FOLLOWED_TICKET_ID = 'practice-ticket-otter'

// What card 3 writes to the wizard. The website lets CompanyAvatar load the logo.
export const PRACTICE_SELECTION = {
  companyId: 'practice-company-otter',
  companyName: 'Otter.ai',
  companyWebsite: 'https://otter.ai',
  productId: 'practice-product-otter',
  productName: 'Otter.ai',
  productWebsite: 'https://otter.ai',
}

const DAY_MS = 24 * 60 * 60 * 1000

// Example requests and approvals leave the directory name and website null, so the directory's
// values win when the query layer enriches them.
const DIRECTORY_FIELDS_UNSET = {
  companyName: null,
  productName: null,
  companyWebsite: null,
  productWebsite: null,
}

// Builds the whole example for one run. The source reads every piece from this one object.
export function buildPracticeExample(startedAt) {
  // The date `days` from the run start. Negative is in the past.
  const at = (days) => new Date(startedAt + days * DAY_MS).toISOString()

  const companies = {
    'practice-company-grammarly': {
      companyId: 'practice-company-grammarly',
      companyName: 'Grammarly',
      website: 'https://grammarly.com',
      listOfProducts: {
        'practice-product-grammarly': {
          productId: 'practice-product-grammarly',
          productName: 'Grammarly',
        },
      },
    },
    'practice-company-canva': {
      companyId: 'practice-company-canva',
      companyName: 'Canva',
      website: 'https://canva.com',
      listOfProducts: {
        'practice-product-canva': { productId: 'practice-product-canva', productName: 'Canva' },
      },
    },
    'practice-company-jasper': {
      companyId: 'practice-company-jasper',
      companyName: 'Jasper',
      website: 'https://jasper.ai',
      listOfProducts: {
        'practice-product-jasper': { productId: 'practice-product-jasper', productName: 'Jasper' },
      },
    },
    'practice-company-midjourney': {
      companyId: 'practice-company-midjourney',
      companyName: 'Midjourney',
      website: 'https://midjourney.com',
      listOfProducts: {
        'practice-product-midjourney': {
          productId: 'practice-product-midjourney',
          productName: 'Midjourney',
        },
      },
    },
    'practice-company-perplexity': {
      companyId: 'practice-company-perplexity',
      companyName: 'Perplexity',
      website: 'https://perplexity.ai',
      listOfProducts: {
        'practice-product-perplexity': {
          productId: 'practice-product-perplexity',
          productName: 'Perplexity',
        },
      },
    },
    'practice-company-otter': {
      companyId: 'practice-company-otter',
      companyName: 'Otter.ai',
      website: 'https://otter.ai',
      listOfProducts: {
        'practice-product-otter': { productId: 'practice-product-otter', productName: 'Otter.ai' },
      },
    },
  }

  // The five requests already on the account: two approved, two denied, one under review.
  const tickets = [
    {
      ticketId: 'practice-ticket-grammarly',
      companyId: 'practice-company-grammarly',
      productId: 'practice-product-grammarly',
      ticketStatus: 'closed',
      approved: true,
      dateCreated: at(-120),
      dateSubmitted: at(-118),
      lastUserUpdate: at(-100),
      ...DIRECTORY_FIELDS_UNSET,
    },
    {
      ticketId: 'practice-ticket-canva',
      companyId: 'practice-company-canva',
      productId: 'practice-product-canva',
      ticketStatus: 'closed',
      approved: true,
      dateCreated: at(-90),
      dateSubmitted: at(-88),
      lastUserUpdate: at(-80),
      ...DIRECTORY_FIELDS_UNSET,
    },
    {
      ticketId: 'practice-ticket-jasper',
      companyId: 'practice-company-jasper',
      productId: 'practice-product-jasper',
      ticketStatus: 'closed',
      approved: false,
      dateCreated: at(-60),
      dateSubmitted: at(-58),
      lastUserUpdate: at(-50),
      ...DIRECTORY_FIELDS_UNSET,
    },
    {
      ticketId: 'practice-ticket-midjourney',
      companyId: 'practice-company-midjourney',
      productId: 'practice-product-midjourney',
      ticketStatus: 'closed',
      approved: false,
      dateCreated: at(-40),
      dateSubmitted: at(-38),
      lastUserUpdate: at(-30),
      ...DIRECTORY_FIELDS_UNSET,
    },
    {
      ticketId: 'practice-ticket-perplexity',
      companyId: 'practice-company-perplexity',
      productId: 'practice-product-perplexity',
      ticketStatus: 'processing',
      approved: null,
      dateCreated: at(-10),
      dateSubmitted: at(-9),
      lastUserUpdate: at(-9),
      ...DIRECTORY_FIELDS_UNSET,
    },
  ]

  // The two approved requests each have an active approval.
  const approvals = [
    {
      approvalId: 'practice-approval-grammarly',
      companyId: 'practice-company-grammarly',
      productId: 'practice-product-grammarly',
      ticketId: 'practice-ticket-grammarly',
      active: true,
      dateCreated: at(-118),
      lastUserConfirmation: at(-20),
      nextUserConfirmation: at(180),
      ...DIRECTORY_FIELDS_UNSET,
    },
    {
      approvalId: 'practice-approval-canva',
      companyId: 'practice-company-canva',
      productId: 'practice-product-canva',
      ticketId: 'practice-ticket-canva',
      active: true,
      dateCreated: at(-88),
      lastUserConfirmation: at(-20),
      nextUserConfirmation: at(180),
      ...DIRECTORY_FIELDS_UNSET,
    },
  ]

  // The four wizard questions. Option keys are the values the answers store.
  const newTicketQuestions = [
    {
      placeholderTxt: 'Describe the work this tool will support',
      questionId: 'practice-question-purpose',
      questionText: 'What will you use this tool for?',
      questionType: QUESTION_TYPES.TEXT,
      options: null,
      required: true,
    },
    {
      placeholderTxt: null,
      questionId: 'practice-question-personal-data',
      questionText: 'Will you enter personal or confidential data?',
      questionType: QUESTION_TYPES.YES_NO,
      options: null,
      required: true,
    },
    {
      placeholderTxt: null,
      questionId: 'practice-question-who-uses',
      questionText: 'Who will use this tool?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: {
        'practice-option-team': 'My team',
        'practice-option-department': 'My department',
      },
      required: true,
    },
    {
      placeholderTxt: null,
      questionId: 'practice-question-file',
      questionText: 'Attach files that support the request',
      questionType: QUESTION_TYPES.FILE_UPLOAD,
      options: null,
      required: false,
    },
  ]

  // The answers the walkthrough fills in on the Explain screen.
  const answers = {
    'practice-question-purpose': 'Summarizing team meeting notes and listing the action items.',
    'practice-question-personal-data': 'no',
    'practice-question-who-uses': { 'practice-option-team': 'My team' },
    'practice-question-file': ['practice-file-meeting-notes'],
  }

  // The submitted request keeps each question with its answer, so archived text still shows.
  const questionsAnswers = newTicketQuestions.map((question) => ({
    questionId: question.questionId,
    questionText: question.questionText,
    questionType: question.questionType,
    required: question.required,
    useInAssessment: false,
    options: question.options,
    answer: answers[question.questionId] ?? null,
  }))

  const file = {
    fileId: 'practice-file-meeting-notes',
    contentType: 'application/pdf',
    filename: 'Meeting-notes-sample.pdf',
    uploadTime: at(0),
    uploadedBy: null,
    questionId: 'practice-question-file',
  }

  // The followed request's account entry, as a draft and once submitted.
  const followedAccountBase = {
    ticketId: PRACTICE_FOLLOWED_TICKET_ID,
    companyId: 'practice-company-otter',
    productId: 'practice-product-otter',
    approved: null,
    dateCreated: at(0),
    lastUserUpdate: at(0),
    ...DIRECTORY_FIELDS_UNSET,
  }

  // The followed request's details: the flat draft, then the submitted version with snapshots.
  const followedDetailBase = {
    ticketId: PRACTICE_FOLLOWED_TICKET_ID,
    approved: null,
    approvalId: null,
    companyId: 'practice-company-otter',
    companyFound: true,
    companySource: 'directory',
    companyName: 'Otter.ai',
    companyWebsite: 'https://otter.ai',
    dateCreated: at(0),
    dateSubmitted: null,
    lastUserUpdate: at(0),
    productFound: true,
    productSource: 'directory',
    productId: 'practice-product-otter',
    productName: 'Otter.ai',
    productWebsite: 'https://otter.ai',
    teamUse: null,
    ticketApprover: null,
    denyReason: null,
    approvalConditions: null,
    answers,
    userMessages: [],
    uploadedFiles: [file],
  }

  // The search for "Otter", in the shape the search returns after refactoring.
  const searchResults = {
    products: [
      {
        productId: 'practice-product-otter',
        productName: 'Otter.ai',
        companyId: 'practice-company-otter',
        companyLegalName: 'Otter.ai, Inc.',
        companyName: 'Otter.ai',
        website: 'https://otter.ai',
        description: 'Meeting transcripts, summaries, and action items.',
        score: 0.96,
        usesAi: true,
      },
    ],
    companies: [
      {
        companyId: 'practice-company-otter',
        companyName: 'Otter.ai',
        companyLegalName: 'Otter.ai, Inc.',
        score: 0.96,
        website: 'https://otter.ai',
        description: 'Makers of Otter.ai, a meeting assistant.',
        listOfProducts: { 'practice-product-otter': 'Otter.ai' },
      },
    ],
  }

  return {
    companies,
    tickets,
    approvals,
    newTicketQuestions,
    followedAccount: {
      draft: { ...followedAccountBase, ticketStatus: 'opened', dateSubmitted: null },
      submitted: { ...followedAccountBase, ticketStatus: 'processing', dateSubmitted: at(0) },
    },
    followedTicket: {
      draft: { ...followedDetailBase, ticketStatus: 'opened', questionsAnswers: [] },
      submitted: {
        ...followedDetailBase,
        ticketStatus: 'processing',
        dateSubmitted: at(0),
        questionsAnswers,
      },
    },
    searchResults,
  }
}
